import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  isUpdatePending,
  rolloverIfUpdatePending,
  subscribeUpdatePending,
  updateBrokeThisTab,
  updatePendingForMs,
} from '@/lib/deployWatcher';

/**
 * How long a newer build waits before the header offers a button. Nothing is
 * broken in the meantime — the tab rolls over silently at the advisor's next
 * navigation, so anyone actually working never sees a button at all. This is
 * only for someone who has been parked on one screen since the deploy, and
 * showing it any sooner just puts a control on screen that nobody asked for
 * (Drew, 2026-09-28: "I hate buttons popping up constantly").
 */
export const PILL_DELAY_MS = 30 * 60 * 1000;

/**
 * Re-renders when the deploy watcher flags a newer build, and again when a
 * chunk then fails (the second notice is what lets the pill drop its wait).
 */
function useUpdatePending(): { pending: boolean; broke: boolean } {
  const [state, setState] = useState(() => ({ pending: isUpdatePending(), broke: updateBrokeThisTab() }));
  useEffect(
    () => subscribeUpdatePending(() => setState({ pending: true, broke: updateBrokeThisTab() })),
    [],
  );
  return state;
}

/**
 * True only once a pending update has been waiting out `PILL_DELAY_MS`.
 * Reads the watcher's own start time rather than counting from mount, so
 * remounting the shell mid-wait doesn't restart the clock — and so a tab that
 * has been stale for an hour offers the button immediately.
 *
 * The wait is for a tab that still works. Once a lazy chunk has failed on
 * this tab, the screen that needed it is already dead and its inline text is
 * pointing at this button, so the button shows at once (the 30 minutes had
 * kept it hidden while the dialog said to click it).
 */
function useUpdateWaitedOut(delayMs: number): boolean {
  const { pending, broke } = useUpdatePending();
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    if (!pending) {
      setWaited(false);
      return;
    }
    const elapsed = updatePendingForMs() ?? 0;
    if (broke || elapsed >= delayMs) {
      setWaited(true);
      return;
    }
    const timer = setTimeout(() => setWaited(true), delayMs - elapsed);
    return () => clearTimeout(timer);
  }, [pending, broke, delayMs]);

  return waited;
}

/**
 * Applies a pending update at the next route change — the screen is being
 * rebuilt anyway, so a real page load there is invisible. Never on first
 * mount, and never mid-impersonation (a reload drops the sandbox). Renders
 * nothing; mounted once inside the router in `App.tsx`.
 */
export function DeployRolloverOnNavigation() {
  const { pathname } = useLocation();
  const { isImpersonating } = useImpersonation();
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!isImpersonating) rolloverIfUpdatePending();
    // Only a pathname change is a navigation — impersonation flips must not fire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}

/**
 * The fallback for someone parked on one screen who never navigates: a quiet
 * button on the right of the app header, and only after the update has gone
 * unapplied for `PILL_DELAY_MS`. Not a modal, not an error colour, and it
 * never reloads on its own — they pick the moment. Rendered by `AppShell`, so
 * it rides the sticky header instead of floating over content.
 */
export function DeployUpdatePill({ delayMs = PILL_DELAY_MS }: { delayMs?: number } = {}) {
  const show = useUpdateWaitedOut(delayMs);
  if (!show) return null;

  return (
    <Button
      size="sm"
      variant="secondary"
      className="ml-auto h-7 gap-2 text-xs"
      data-testid="deploy-update-notice"
      onClick={() => window.location.reload()}
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Update ready — reload
    </Button>
  );
}
